import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DataList,
  DataListAction,
  DataListCell,
  DataListContent,
  DataListItem,
  DataListItemCells,
  DataListItemRow,
  DataListToggle,
  Divider,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  LabelGroup,
  MenuToggle,
  MenuToggleElement,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, ExternalLinkAltIcon, FilmIcon, FilterIcon, SyncIcon } from '@patternfly/react-icons';
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import { parseSessionDetail, parseSessionListResponse } from 'src/contracts/monitor';
import { Link } from 'react-router-dom';
import { buildClerkUserUrl } from './clerkUserLink';
import './sessions.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface AttemptResult {
  kind: string;
  score?: number | null;
  duration_ms?: number | null;
}

interface SessionAttempt {
  id: string;
  attempt_no: number;
  state: string;
  started_at?: string | null;
  ended_at?: string | null;
  result?: AttemptResult | null;
}

interface SessionResult {
  kind: string;
  score?: number | null;
  duration_ms?: number | null;
}

interface SessionRecord {
  id: string;
  user_id?: string | null;
  user_email?: string | null;
  user_label?: string | null;
  clerk_external_user_id?: string | null;
  robot_id: string;
  robot_name?: string | null;
  game_type_key?: string | null;
  state: string;
  end_reason?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  attempt_count?: number | null;
  attempts?: SessionAttempt[] | null;
  result?: SessionResult | null;
  ticket_state?: string | null;
  queue_position?: number | null;
  recording_status?: string | null;
}

interface RecordingJob {
  id: string;
  status: string;
  camera_type: string;
  media_role: string;
  started_at?: string | null;
  ended_at?: string | null;
  track_name?: string | null;
  camera_key?: string | null;
  egress_id?: string | null;
  object_key_prefix?: string | null;
  bucket?: string | null;
}

interface SessionMedia {
  id: string;
  camera_type: string;
  camera_key: string;
  media_role: string;
  source: string;
  source_ref?: string | null;
  storage_provider: string;
  bucket: string;
  object_key: string;
  content_type?: string | null;
  duration_ms?: number | null;
  status?: string | null;
  media_url?: string | null;
}

interface SessionDetail extends SessionRecord {
  recording_jobs?: RecordingJob[] | null;
  media?: SessionMedia[] | null;
}

const renderClerkIdentity = (externalUserId?: string | null) => {
  if (!externalUserId) {
    return '—';
  }
  const clerkUserUrl = buildClerkUserUrl(externalUserId);
  if (!clerkUserUrl) {
    return externalUserId;
  }
  return (
    <a
      className="session-clerk-link"
      href={clerkUserUrl}
      target="_blank"
      rel="noreferrer"
    >
      {externalUserId}
      <ExternalLinkAltIcon />
    </a>
  );
};

const buildDemoSessions = (): SessionRecord[] => {
  const now = Date.now();
  const iso = (offsetMs: number) => new Date(now - offsetMs).toISOString();

  return [
    {
      id: 'sess_demo_01',
      user_id: 'user_1',
      user_email: 'ava@claw.co',
      user_label: 'ava@claw.co',
      robot_id: 'robot_alpha',
      robot_name: 'Alpha-01',
      game_type_key: 'claw_v1',
      state: 'active',
      started_at: iso(4 * 60 * 1000),
      created_at: iso(5 * 60 * 1000),
      attempt_count: 2,
      attempts: [
        {
          id: 'att_01',
          attempt_no: 0,
          state: 'ended',
          started_at: iso(4 * 60 * 1000),
          ended_at: iso(3 * 60 * 1000 + 20 * 1000),
          result: { kind: 'lose', score: 0 },
        },
        {
          id: 'att_02',
          attempt_no: 1,
          state: 'active',
          started_at: iso(90 * 1000),
          result: null,
        },
      ],
    },
    {
      id: 'sess_demo_02',
      user_id: 'user_2',
      user_email: 'guest_9183@example.com',
      user_label: 'guest_9183',
      robot_id: 'robot_beta',
      robot_name: 'Beta-07',
      game_type_key: 'basketball_v1',
      state: 'ended',
      end_reason: 'completed',
      created_at: iso(38 * 60 * 1000),
      started_at: iso(36 * 60 * 1000),
      ended_at: iso(30 * 60 * 1000),
      attempt_count: 10,
      result: { kind: 'score', score: 7 },
      attempts: [
        {
          id: 'att_21',
          attempt_no: 0,
          state: 'ended',
          started_at: iso(36 * 60 * 1000),
          ended_at: iso(35 * 60 * 1000 + 40 * 1000),
          result: { kind: 'score', score: 1 },
        },
        {
          id: 'att_22',
          attempt_no: 1,
          state: 'ended',
          started_at: iso(35 * 60 * 1000),
          ended_at: iso(34 * 60 * 1000 + 30 * 1000),
          result: { kind: 'score', score: 0 },
        },
      ],
    },
    {
      id: 'sess_demo_03',
      user_id: null,
      user_email: null,
      user_label: 'anonymous',
      robot_id: 'robot_gamma',
      robot_name: 'Gamma-02',
      game_type_key: 'maze_v1',
      state: 'connecting',
      created_at: iso(2 * 60 * 1000),
      started_at: null,
      attempt_count: 0,
      attempts: [],
    },
    {
      id: 'sess_demo_04',
      user_id: 'user_9',
      user_email: 'kai@claw.co',
      user_label: 'kai@claw.co',
      robot_id: 'robot_delta',
      robot_name: 'Delta-09',
      game_type_key: 'claw_v1',
      state: 'ended',
      end_reason: 'expired',
      created_at: iso(90 * 60 * 1000),
      started_at: iso(88 * 60 * 1000),
      ended_at: iso(87 * 60 * 1000),
      attempt_count: 1,
      result: { kind: 'lose' },
      attempts: [
        {
          id: 'att_81',
          attempt_no: 0,
          state: 'ended',
          started_at: iso(88 * 60 * 1000),
          ended_at: iso(87 * 60 * 1000),
          result: { kind: 'lose' },
        },
      ],
    },
  ];
};

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const formatDuration = (start?: string | null, end?: string | null) => {
  if (!start) {
    return '—';
  }
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return '—';
  }
  const delta = Math.max(0, endMs - startMs);
  const minutes = Math.floor(delta / 60000);
  const seconds = Math.floor((delta % 60000) / 1000);
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const formatAttemptCount = (session: SessionRecord) => {
  if (typeof session.attempt_count === 'number') {
    return session.attempt_count;
  }
  return session.attempts?.length ?? 0;
};

const getStateColor = (state: string) => {
  switch (state.toLowerCase()) {
    case 'active':
      return 'green';
    case 'connecting':
    case 'reserved':
      return 'blue';
    case 'ended':
      return 'grey';
    case 'canceled':
    case 'expired':
      return 'red';
    default:
      return 'orange';
  }
};

const getResultColor = (kind?: string | null) => {
  if (!kind) {
    return 'grey';
  }
  switch (kind.toLowerCase()) {
    case 'win':
      return 'green';
    case 'lose':
      return 'red';
    case 'score':
      return 'blue';
    case 'time':
      return 'orange';
    default:
      return 'grey';
  }
};

const getStateLabel = (state: string) => state.replace(/_/g, ' ');
const getRecordingLabel = (status?: string | null) => (status ? status.replace(/_/g, ' ') : '—');
const getRecordingColor = (status?: string | null) => {
  if (!status) {
    return 'grey';
  }
  switch (status.toLowerCase()) {
    case 'recording':
      return 'blue';
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    default:
      return 'orange';
  }
};

const isVideo = (media: SessionMedia) => {
  if (media.content_type && media.content_type.startsWith('video/')) {
    return true;
  }
  const key = media.object_key.toLowerCase();
  return key.endsWith('.mp4') || key.endsWith('.webm') || key.endsWith('.mov');
};

const SessionsDashboard = () => {
  const authedFetch = useAuthedFetch();
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingDemo, setUsingDemo] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [sessionDetails, setSessionDetails] = useState<Map<string, SessionDetail>>(new Map());
  const [detailsLoadingId, setDetailsLoadingId] = useState<string | null>(null);
  const [detailsErrors, setDetailsErrors] = useState<Map<string, string>>(new Map());

  const [searchText, setSearchText] = useState('');
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [isGameTypeOpen, setIsGameTypeOpen] = useState(false);
  const [selectedGameTypes, setSelectedGameTypes] = useState<string[]>([]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authedFetch(`${API_BASE}/api/monitor/sessions/?include_attempts=true`);
      if (!response.ok) {
        throw new Error(`Failed to load sessions (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parseSessionListResponse(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      setSessions(parsed.value);
      setError(null);
      setUsingDemo(false);
    } catch (err) {
      setError((err as Error).message);
      setSessions(buildDemoSessions());
      setUsingDemo(true);
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [authedFetch]);

  const fetchSessionDetails = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      return;
    }
    if (sessionDetails.has(sessionId)) {
      return;
    }
    setDetailsLoadingId(sessionId);
    try {
      const response = await authedFetch(`${API_BASE}/api/monitor/sessions/${sessionId}/?include_attempts=true`);
      if (!response.ok) {
        throw new Error(`Failed to load session (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parseSessionDetail(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      const data = parsed.value;
      setSessionDetails((prev) => {
        const next = new Map(prev);
        next.set(sessionId, data);
        return next;
      });
      setDetailsErrors((prev) => {
        if (!prev.has(sessionId)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(sessionId);
        return next;
      });
    } catch (err) {
      const message = (err as Error).message;
      setDetailsErrors((prev) => {
        const next = new Map(prev);
        next.set(sessionId, message);
        return next;
      });
    } finally {
      setDetailsLoadingId(null);
    }
  }, [authedFetch, sessionDetails]);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    if (!expandedSessionId) {
      return;
    }
    void fetchSessionDetails(expandedSessionId);
  }, [expandedSessionId, fetchSessionDetails]);

  const stateOptions = useMemo(() => {
    const values = new Set<string>();
    sessions.forEach((session) => values.add(session.state));
    return Array.from(values).sort();
  }, [sessions]);

  const gameTypeOptions = useMemo(() => {
    const values = new Set<string>();
    sessions.forEach((session) => {
      if (session.game_type_key) {
        values.add(session.game_type_key);
      }
    });
    return Array.from(values).sort();
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return sessions.filter((session) => {
      if (search) {
        const haystack = [
          session.id,
          session.robot_id,
          session.robot_name,
          session.user_id,
          session.user_email,
          session.user_label,
          session.clerk_external_user_id,
          session.game_type_key,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }
      if (selectedStates.length > 0 && !selectedStates.includes(session.state)) {
        return false;
      }
      if (selectedGameTypes.length > 0 && session.game_type_key) {
        if (!selectedGameTypes.includes(session.game_type_key)) {
          return false;
        }
      }
      return true;
    });
  }, [sessions, searchText, selectedStates, selectedGameTypes]);

  const summary = useMemo(() => {
    const activeStates = new Set(['reserved', 'connecting', 'active']);
    const activeSessions = sessions.filter((session) => activeStates.has(session.state));
    const endedSessions = sessions.filter((session) => session.state === 'ended');
    const attemptTotals = sessions.reduce((sum, session) => sum + formatAttemptCount(session), 0);
    const attemptWins = sessions.reduce((sum, session) => {
      const attempts = session.attempts || [];
      const wins = attempts.filter((attempt) => attempt.result?.kind?.toLowerCase() === 'win').length;
      return sum + wins;
    }, 0);
    const avgDurationMs = endedSessions.length
      ? endedSessions.reduce((sum, session) => {
          const start = session.started_at ? new Date(session.started_at).getTime() : NaN;
          const end = session.ended_at ? new Date(session.ended_at).getTime() : NaN;
          if (Number.isNaN(start) || Number.isNaN(end)) {
            return sum;
          }
          return sum + Math.max(0, end - start);
        }, 0) / endedSessions.length
      : 0;
    const avgDurationText = avgDurationMs
      ? `${Math.round(avgDurationMs / 60000)}m ${Math.round((avgDurationMs % 60000) / 1000)}s`
      : '—';

    return {
      active: activeSessions.length,
      total: sessions.length,
      ended: endedSessions.length,
      attemptTotals,
      attemptWins,
      successRate: attemptTotals ? Math.round((attemptWins / attemptTotals) * 100) : 0,
      avgDurationText,
    };
  }, [sessions]);

  const toggleExpand = (sessionId: string) => {
    setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
  };

  return (
    <div className="sessions-page">
      <PageHeader title="Session Monitor" subtitle="Live sessions, attempts, and outcomes" />

      <div className="session-monitor-hero">
        <div>
          <Title headingLevel="h2" size="lg">
            Live Game Operations
          </Title>
          <Content>
            <p>
              Monitor active sessions, attempts, and outcomes across the fleet.
            </p>
          </Content>
        </div>
        <div className="session-hero-meta">
          {lastUpdated && (
            <Label color="blue">Updated {lastUpdated.toLocaleTimeString()}</Label>
          )}
          {usingDemo && <Label color="orange">Demo data</Label>}
          {error && <Label color="red">{error}</Label>}
          <Button variant="secondary" icon={<SyncIcon />} onClick={fetchSessions}>
            Refresh
          </Button>
        </div>
      </div>

      <Grid hasGutter className="session-summary-grid">
        <GridItem sm={6} lg={3}>
          <Card className="session-summary-card">
            <CardTitle>Active Sessions</CardTitle>
            <CardBody>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }}>
                <FlexItem>
                  <Title headingLevel="h2" size="2xl">
                    {summary.active}
                  </Title>
                  <small>{summary.total} total</small>
                </FlexItem>
                <FlexItem>
                  <Label color="green" icon={<CheckCircleIcon />}>Live</Label>
                </FlexItem>
              </Flex>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem sm={6} lg={3}>
          <Card className="session-summary-card">
            <CardTitle>Attempts Logged</CardTitle>
            <CardBody>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }}>
                <FlexItem>
                  <Title headingLevel="h2" size="2xl">
                    {summary.attemptTotals}
                  </Title>
                  <small>Wins: {summary.attemptWins}</small>
                </FlexItem>
                <FlexItem>
                  <Label color="blue" icon={<ClockIcon />}>Rolling</Label>
                </FlexItem>
              </Flex>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem sm={6} lg={3}>
          <Card className="session-summary-card">
            <CardTitle>Success Rate</CardTitle>
            <CardBody>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }}>
                <FlexItem>
                  <Title headingLevel="h2" size="2xl">
                    {summary.successRate}%
                  </Title>
                  <small>Attempts with wins</small>
                </FlexItem>
                <FlexItem>
                  <Label color={summary.successRate >= 50 ? 'green' : 'orange'}>Trend</Label>
                </FlexItem>
              </Flex>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem sm={6} lg={3}>
          <Card className="session-summary-card">
            <CardTitle>Avg Duration</CardTitle>
            <CardBody>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }}>
                <FlexItem>
                  <Title headingLevel="h2" size="2xl">
                    {summary.avgDurationText}
                  </Title>
                  <small>Ended sessions</small>
                </FlexItem>
                <FlexItem>
                  <Label color="grey" icon={<ExclamationCircleIcon />}>Latency</Label>
                </FlexItem>
              </Flex>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      <Card className="session-list-card">
        <CardTitle>
          <Flex alignItems={{ default: 'alignItemsCenter' }} justifyContent={{ default: 'justifyContentSpaceBetween' }}>
            <FlexItem>
              <Title headingLevel="h3" size="lg">Sessions</Title>
            </FlexItem>
            <FlexItem>
              <Label color="grey" icon={<FilterIcon />}>{filteredSessions.length} shown</Label>
            </FlexItem>
          </Flex>
        </CardTitle>
        <CardBody>
          <Toolbar id="sessions-toolbar" clearAllFilters={() => {
            setSearchText('');
            setSelectedStates([]);
            setSelectedGameTypes([]);
          }}>
            <ToolbarContent>
              <ToolbarGroup>
                <ToolbarItem>
                  <SearchInput
                    aria-label="Search sessions"
                    value={searchText}
                    onChange={(_, value) => setSearchText(value)}
                    placeholder="Search by session, robot, user, or Clerk ID"
                  />
                </ToolbarItem>
              </ToolbarGroup>
              <ToolbarGroup>
                <ToolbarItem>
                  <Select
                    role="menu"
                    toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                      <MenuToggle ref={toggleRef} isExpanded={isStateOpen} onClick={() => setIsStateOpen((prev) => !prev)}>
                        State
                        {selectedStates.length > 0 && <Badge isRead>{selectedStates.length}</Badge>}
                      </MenuToggle>
                    )}
                    isOpen={isStateOpen}
                    selected={selectedStates}
                    onSelect={(_, value) => {
                      if (typeof value === 'string') {
                        setSelectedStates((prev) =>
                          prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
                        );
                      }
                    }}
                    onOpenChange={(isOpen) => setIsStateOpen(isOpen)}
                  >
                    <SelectList>
                      {stateOptions.map((option) => (
                        <SelectOption key={option} value={option} hasCheckbox isSelected={selectedStates.includes(option)}>
                          {getStateLabel(option)}
                        </SelectOption>
                      ))}
                    </SelectList>
                  </Select>
                </ToolbarItem>
                <ToolbarItem>
                  <Select
                    role="menu"
                    toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                      <MenuToggle ref={toggleRef} isExpanded={isGameTypeOpen} onClick={() => setIsGameTypeOpen((prev) => !prev)}>
                        Game
                        {selectedGameTypes.length > 0 && <Badge isRead>{selectedGameTypes.length}</Badge>}
                      </MenuToggle>
                    )}
                    isOpen={isGameTypeOpen}
                    selected={selectedGameTypes}
                    onSelect={(_, value) => {
                      if (typeof value === 'string') {
                        setSelectedGameTypes((prev) =>
                          prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
                        );
                      }
                    }}
                    onOpenChange={(isOpen) => setIsGameTypeOpen(isOpen)}
                  >
                    <SelectList>
                      {gameTypeOptions.map((option) => (
                        <SelectOption key={option} value={option} hasCheckbox isSelected={selectedGameTypes.includes(option)}>
                          {option}
                        </SelectOption>
                      ))}
                    </SelectList>
                  </Select>
                </ToolbarItem>
              </ToolbarGroup>
            </ToolbarContent>
          </Toolbar>

          <Divider className="session-divider" />

          {loading ? (
            <div className="session-loading">
              <Spinner size="xl" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <EmptyState>
              <Title headingLevel="h4">No sessions found</Title>
              <EmptyStateBody>Try adjusting filters or search.</EmptyStateBody>
            </EmptyState>
          ) : (
            <DataList aria-label="Sessions list" isCompact>
              {filteredSessions.map((session) => {
                const isExpanded = expandedSessionId === session.id;
                const attempts = session.attempts || [];
                const attemptCount = formatAttemptCount(session);
                const lastAttempt = attempts[attempts.length - 1];
                const details = sessionDetails.get(session.id);
                const detailsError = detailsErrors.get(session.id);
                const media = details?.media || [];
                const recordingJobs = details?.recording_jobs || [];
                const isDetailsLoading = detailsLoadingId === session.id && !details;
                return (
                  <DataListItem key={session.id} aria-labelledby={`session-${session.id}`} isExpanded={isExpanded}>
                    <DataListItemRow>
                      <DataListToggle
                        aria-controls={`session-content-${session.id}`}
                        id={`session-toggle-${session.id}`}
                        isExpanded={isExpanded}
                        onClick={() => toggleExpand(session.id)}
                      />
                      <DataListItemCells
                        dataListCells={[
                          <DataListCell key="primary">
                            <div className="session-primary" id={`session-${session.id}`}>
                              <Title headingLevel="h4" size="md">{session.id}</Title>
                              <LabelGroup>
                                <Label color={getStateColor(session.state)}>{getStateLabel(session.state)}</Label>
                                {session.game_type_key && <Label color="blue">{session.game_type_key}</Label>}
                                {session.result?.kind && (
                                  <Label color={getResultColor(session.result.kind)}>{session.result.kind}</Label>
                                )}
                                {typeof session.queue_position === 'number' && (
                                  <Label color="blue">Queue #{session.queue_position}</Label>
                                )}
                                {session.recording_status && (
                                  <Label color={getRecordingColor(session.recording_status)}>
                                    {getRecordingLabel(session.recording_status)}
                                  </Label>
                                )}
                              </LabelGroup>
                            </div>
                          </DataListCell>,
                          <DataListCell key="robot">
                            <Content>
                              <small>Robot</small>
                              <Title headingLevel="h4" size="md">
                                {session.robot_name || session.robot_id}
                              </Title>
                              <small>User: {session.user_label || session.user_id || '—'}</small>
                              <small>Email: {session.user_email || '—'}</small>
                              <small>Clerk: {renderClerkIdentity(session.clerk_external_user_id)}</small>
                            </Content>
                          </DataListCell>,
                          <DataListCell key="attempts">
                            <Content>
                              <small>Attempts</small>
                              <Title headingLevel="h4" size="md">{attemptCount}</Title>
                              <small>
                                Last: {lastAttempt?.state ? getStateLabel(lastAttempt.state) : '—'}
                              </small>
                            </Content>
                          </DataListCell>,
                          <DataListCell key="timing">
                            <Content>
                              <small>Started</small>
                              <Title headingLevel="h4" size="md">{formatDuration(session.started_at, session.ended_at)}</Title>
                              <small>{formatTimestamp(session.started_at)}</small>
                            </Content>
                          </DataListCell>,
                        ]}
                      />
                      <DataListAction
                        id={`session-${session.id}-actions`}
                        aria-label="Session actions"
                        aria-labelledby={`session-${session.id}`}
                      >
                        <Link to={`/sessions/${session.id}`} className="session-details-link">
                          <Button variant="secondary">Details</Button>
                        </Link>
                      </DataListAction>
                    </DataListItemRow>
                    <DataListContent
                      aria-label="Session details"
                      id={`session-content-${session.id}`}
                      isHidden={!isExpanded}
                    >
                      <div className="session-details">
                        <Grid hasGutter>
                          <GridItem sm={12} lg={4}>
                            <Card isCompact>
                              <CardTitle>Session Details</CardTitle>
                              <CardBody>
                                <Content>
                                  <p><strong>Robot:</strong> {session.robot_name || session.robot_id}</p>
                                  <p><strong>User:</strong> {session.user_label || session.user_id || '—'}</p>
                                  <p><strong>Email:</strong> {session.user_email || '—'}</p>
                                  <p><strong>Clerk:</strong> {renderClerkIdentity(session.clerk_external_user_id)}</p>
                                  <p><strong>Created:</strong> {formatTimestamp(session.created_at)}</p>
                                  <p><strong>Started:</strong> {formatTimestamp(session.started_at)}</p>
                                  <p><strong>Ended:</strong> {formatTimestamp(session.ended_at)}</p>
                                  <p><strong>End reason:</strong> {session.end_reason || '—'}</p>
                                </Content>
                              </CardBody>
                            </Card>
                          </GridItem>
                          <GridItem sm={12} lg={8}>
                            <Card isCompact>
                              <CardTitle>Attempts</CardTitle>
                              <CardBody>
                                {attempts.length === 0 ? (
                                  <EmptyState>
                                    <EmptyStateBody>No attempts recorded.</EmptyStateBody>
                                  </EmptyState>
                                ) : (
                                  <div className="attempt-grid">
                                    {attempts.map((attempt) => (
                                      <div key={attempt.id} className="attempt-card">
                                        <div className="attempt-header">
                                          <span>#{attempt.attempt_no + 1}</span>
                                          <Label color={getStateColor(attempt.state)}>{getStateLabel(attempt.state)}</Label>
                                        </div>
                                        <div className="attempt-meta">
                                          <div>
                                            <small>Started</small>
                                            <div>{formatTimestamp(attempt.started_at)}</div>
                                          </div>
                                          <div>
                                            <small>Duration</small>
                                            <div>{formatDuration(attempt.started_at, attempt.ended_at)}</div>
                                          </div>
                                        </div>
                                        <Divider />
                                        <div className="attempt-result">
                                          <small>Result</small>
                                          {attempt.result?.kind ? (
                                            <Label color={getResultColor(attempt.result.kind)}>{attempt.result.kind}</Label>
                                          ) : (
                                            <span>—</span>
                                          )}
                                          {typeof attempt.result?.score === 'number' && (
                                            <span className="attempt-score">Score: {attempt.result?.score}</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </GridItem>
                          <GridItem sm={12}>
                            <Card isCompact>
                              <CardTitle>Recordings</CardTitle>
                              <CardBody>
                                {isDetailsLoading ? (
                                  <div className="session-loading">
                                    <Spinner size="lg" />
                                  </div>
                                ) : detailsError && !details ? (
                                  <EmptyState>
                                    <EmptyStateBody>{detailsError}</EmptyStateBody>
                                  </EmptyState>
                                ) : media.length > 0 ? (
                                  <div className="session-media-grid">
                                    {media.map((item) => (
                                      <div key={item.id} className="session-media-card">
                                        <div className="session-media-header">
                                          <LabelGroup>
                                            <Label color="blue">{item.media_role}</Label>
                                            <Label color="grey">{item.camera_key}</Label>
                                            {item.status && <Label color="green">{item.status}</Label>}
                                          </LabelGroup>
                                          <Badge isRead>{item.storage_provider}</Badge>
                                        </div>
                                        <div className="session-media-body">
                                          {item.media_url && isVideo(item) ? (
                                            <video className="session-media-video" controls src={item.media_url} />
                                          ) : (
                                            <div className="session-media-placeholder">
                                              <FilmIcon />
                                              <small>No preview available</small>
                                            </div>
                                          )}
                                        </div>
                                        <div className="session-media-footer">
                                          <small>{item.bucket}/{item.object_key}</small>
                                          {item.media_url ? (
                                            <Button
                                              variant="link"
                                              component="a"
                                              href={item.media_url}
                                              target="_blank"
                                              rel="noreferrer"
                                              icon={<ExternalLinkAltIcon />}
                                            >
                                              Watch
                                            </Button>
                                          ) : (
                                            <Label color="orange">No public URL</Label>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <EmptyState>
                                    <EmptyStateBody>No recordings available yet.</EmptyStateBody>
                                  </EmptyState>
                                )}
                                {recordingJobs.length > 0 && (
                                  <>
                                    <Divider className="session-divider" />
                                    <div className="recording-job-list">
                                      {recordingJobs.map((job) => (
                                        <div key={job.id} className="recording-job-row">
                                          <div>
                                            <Title headingLevel="h4" size="md">{job.camera_key || job.camera_type}</Title>
                                            <small>{job.media_role}</small>
                                          </div>
                                          <div>
                                            <Label color={getRecordingColor(job.status)}>{job.status}</Label>
                                            <small>Started: {formatTimestamp(job.started_at)}</small>
                                            <small>Ended: {formatTimestamp(job.ended_at)}</small>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </CardBody>
                            </Card>
                          </GridItem>
                        </Grid>
                      </div>
                    </DataListContent>
                  </DataListItem>
                );
              })}
            </DataList>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default SessionsDashboard;
